import {useState} from "react";
import {
  Avatar,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow, Typography
} from "@mui/material";
import {Box, Stack} from "@mui/system";
import IconButton from "@mui/material/IconButton";
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import HourglassTopIcon from '@mui/icons-material/HourglassTop';
import {BreadLoafIcon} from "../icon/BreadLoafIcon";
import {DropCalendarDialog} from "./DropCalendarDialog";
import {getISODateFormat} from "../utils/DateUtils";
import {dropEvents, generalInfo, pickupStart, preEnd, preStart} from "./DropEvents";
import {HourglassBottom} from "@mui/icons-material";

const monthOfYear = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

export const BREAD_BROWN = '#db9153'
export const ADJACENT_MONTH_BACKGROUND = '#DCDCDC'  // lighter: '#ECECEC'
export const DAYOFWEEK_BACKGROUND =  'rgba(255,219,119,0.73)'
export const MONTH_DATE_NUMBER = '#575757'
export const PREORDER_START = '#b9c442'
export const PREORDER_STOP = '#ee816d'

const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const monthViewRows = 6

function DropCalendar() {
  const [viewDate, setViewDate] = useState(new Date())
  const [dialogVisible, setDialogVisible] = useState(false)
  const [eventTitle, setEventTitle] = useState(undefined)
  const [eventDescription, setEventDescription] =  useState(undefined)
  const [eventItems, setEventItems] = useState(undefined)

  const currentDate = new Date()

  function getViewYear() {
    return viewDate.getFullYear()
  }

  function getViewMonthNum() {
    return viewDate.getMonth() + 1
  }

  function isTodaysDate(year, monthNum, monthDate) {
    const today = currentDate
    return year === today.getFullYear() && monthNum === (today.getMonth() + 1) && monthDate === today.getDate()
  }

  function updateViewDate(year, monthNum) {
    setViewDate(new Date(year, monthNum - 1, 1, 12, 0, 0))
  }

  function navigatePreviousMonth() {
    const currMonthNum = getViewMonthNum()
    const currYear = getViewYear()
    if (currMonthNum === 1) {
      updateViewDate(currYear - 1, 12)
    } else {
      updateViewDate(currYear, currMonthNum - 1)
    }
  }

  function navigateNextMonth() {
    const currMonthNum = getViewMonthNum()
    const currYear = getViewYear()

    if (currMonthNum === 12) {
      updateViewDate(currYear + 1, 1)
    } else {
      updateViewDate(currYear, currMonthNum + 1)
    }

  }

  // const year = currentDate.getFullYear();
  // const month = currentDate.getMonth(); // 0-indexed (0 = Jan, 11 = Dec)

  function getDaysInMonth(year, monthNum) {
    // days in month is obtained by providing day 0, which backs up one day: previous month's final day,
    // so in this case we use monthIx + 1, which is monthNum as being used here
    return new Date(year, monthNum, 0).getDate();
  }

  function getFirstDayOfMonth(year, monthNum) {
    // first day of week for the month is obtained by providing day 1,
    // but use noon for a time to buffer against timezone which could back up a day...
    return new Date(year, monthNum - 1, 1, 12, 0, 0).getDay();
  }

  function createGridItem(year, monthNum, monthDate, outOfView, dayIx) {
    const dateKey = getISODateFormat(new Date(year, monthNum - 1, monthDate))
    let event = undefined
    if (Object.keys(dropEvents).includes(dateKey)) {
      event = dropEvents[dateKey]
    }
    let gridItem = {year: year, monthNum: monthNum, monthDate: monthDate, outOfView: outOfView, dayOfWeekIx: dayIx}
    if (event) {
      gridItem = {...gridItem, event: event}
    }
    return gridItem
  }

  function createMonthGridData(year, monthNum) {
    const daysInMonth = getDaysInMonth(year, monthNum)
    const firstDayOfWeekIx = getFirstDayOfMonth(year, monthNum)

    const grid = []

    // special case for row 0...
    let rowNum = 0
    grid[rowNum] = []
    let previousMonthLastDay = undefined
    let dayOfWeekIx = 0
    if (firstDayOfWeekIx > 0) {
      const previousMonthNum = monthNum === 0 ? 12 : monthNum - 1
      const previousMonthYear = monthNum === 0 ? year - 1 : year
      previousMonthLastDay = getDaysInMonth(previousMonthYear, previousMonthNum)
      const previousMonthFirstDay = previousMonthLastDay - (firstDayOfWeekIx - 1)
      for (let prevMonthDate = previousMonthFirstDay; prevMonthDate <= previousMonthLastDay; prevMonthDate++) {
        grid[rowNum].push(createGridItem(previousMonthYear, previousMonthNum, prevMonthDate, true, dayOfWeekIx++))
      }
    }
    // begin in-view month, row 0...
    let monthDate = 1
    while (dayOfWeekIx < 7) {
      grid[rowNum].push(createGridItem(year, monthNum, monthDate, false, dayOfWeekIx++))
      ++monthDate
    }

    let nextMonthNum = undefined
    let nextMonthYear = undefined
    let nextMonthDate = undefined
    while (rowNum < monthViewRows && monthDate <= daysInMonth) {
      ++rowNum
      grid[rowNum] = []
      dayOfWeekIx = 0;
      while (dayOfWeekIx < 7 && monthDate <= daysInMonth) {
        grid[rowNum].push(createGridItem(year, monthNum, monthDate, false, dayOfWeekIx++))
        ++monthDate
      }
      if (dayOfWeekIx < 7) {
        nextMonthNum = monthNum === 12 ? 1 : monthNum + 1
        nextMonthYear = monthNum === 12 ? year + 1 : year
        nextMonthDate = 1
        while (dayOfWeekIx < 7) {
          grid[rowNum].push(createGridItem(nextMonthYear, nextMonthNum, nextMonthDate, true, dayOfWeekIx++))
          ++nextMonthDate
        }
      }
    }

    while (++rowNum < monthViewRows) {
      grid[rowNum] = []
      dayOfWeekIx = 0;
      if (nextMonthDate === undefined) {
        nextMonthNum = monthNum === 12 ? 1 : monthNum + 1
        nextMonthYear = monthNum === 12 ? year + 1 : year
        nextMonthDate = 1
      }
      while (dayOfWeekIx < 7) {
        grid[rowNum].push(createGridItem(nextMonthYear, nextMonthNum, nextMonthDate++, true, dayOfWeekIx++))
      }
    }
    return grid
  }

  function renderViewToolbar(year, monthNum) {
    return <Grid container justifyContent={'space-between'}>
      <Grid justifyContent={'center'}>
      <IconButton onClick={navigatePreviousMonth}
                  sx={{marginRight: 1, backgroundColor: BREAD_BROWN,
                    boxShadow: 3,
                    '&:hover': {
                      backgroundColor: 'grey.100',       // Slight darken on hover
                      boxShadow: 6,                      // Increases elevation on hover
                    }}}><ArrowBackIosIcon fontSize={'medium'}/></IconButton>
      </Grid>
      <Grid justifyContent={'center'}>
      <Box sx={{fontSize: 28, display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
        {monthOfYear[monthNum - 1]}&nbsp; &nbsp; {year}
      </Box>
      </Grid>
      <Grid justifyContent={'center'}>
      <IconButton onClick={navigateNextMonth}
                  sx={{marginLeft: 1, backgroundColor: BREAD_BROWN,
                    boxShadow: 3,
                    '&:hover': {
                      backgroundColor: 'grey.100',       // Slight darken on hover
                      boxShadow: 6,                      // Increases elevation on hover
                    }}}><ArrowForwardIosIcon/></IconButton>
      </Grid>
    </Grid>
  }

  function renderDaysOfWeekHeader() {
    return (
            <TableRow>
              {weekDays.map((v,i) => {
                return <TableCell key={i} align={'center'} sx={{p: 1, width: 35, height: 25,
                  backgroundColor: DAYOFWEEK_BACKGROUND, border: '1px solid black', '& + &': {borderLeft: 'none'}}}>
                  {v}
                </TableCell>
              })}
            </TableRow>
    )
  }

  function renderMonthTableView(grid) {
    return (
            <TableContainer component={Paper}>
              <Table sx={{minWidth: 370,
                '& .MuiTableCell-head': {
                  borderLeft: 'none', borderTop: 'none'
                },

                '& .MuiTableCell-head:first-of-type, & .MuiTableCell-body:first-of-type': {
                  borderLeft: 'none', borderTop: 'none'
                },
                '& .MuiTableCell-head:last-of-type, & .MuiTableCell-body:last-of-type': {
                  borderRight: 'none', borderTop: 'none'
                },
                '& .MuiTableRow-root:last-child .MuiTableCell-body': {
                  borderBottom: 'none',
                }, }} area-label="simple table">
              <TableHead>
                {renderDaysOfWeekHeader()}
              </TableHead>
              <TableBody>
              {[0, 1, 2, 3, 4, 5].map((r,i) => {
                return (
                        <TableRow key={r}>
                          {renderWeekTableRow(grid, r)}
                        </TableRow>)
              })}
              </TableBody>
              </Table>
            </TableContainer>
    )
  }

  function renderWeekTableRow(grid, rowNum) {
    return (
            [0,1,2,3,4,5,6].map((d,i) => {
              return renderDateBox(`${rowNum}-${d}`, grid[rowNum][d])
            })
    )
  }

  function showDialog(gridItem) {
    setEventTitle(gridItem['event']['title'])
    setEventDescription(gridItem['event']['description'])
    if (Object.hasOwn(gridItem['event'], 'items')) {
      setEventItems(gridItem['event']['items'])
    } else if (Object.hasOwn(gridItem['event'], 'dropRef')) {
      setEventItems([`Drop: ${gridItem['event']['dropRef']}`])
    }
    setDialogVisible(true)
  }

  function dismissDialog() {
    setDialogVisible(false)
    // setEventTitle(undefined)
    // setEventDescription(undefined)
    // setEventItems(undefined)
  }

  function renderDropEventContent(gridItem) {
    if (! Object.hasOwn(gridItem, 'event')) {
      return null
    }
    if (gridItem['event']['type'] === generalInfo) {
      return (<Typography flexWrap={'wrap'} fontStyle={'italic'}
                          fontWeight={700} fontSize={6}>{gridItem['event']['title']}</Typography>)
    }
    if (gridItem['event']['type'] === preStart) {
      return (
        <Avatar sx={{alignContent: 'right', color: 'black', bgcolor: PREORDER_START, width: 24, height: 24}}>
          <HourglassTopIcon fontSize={'small'}/>
        </Avatar>
      )
    }
    if (gridItem['event']['type'] === preEnd) {
      return (
        <Avatar sx={{alignContent: 'right', color: 'black', bgcolor: PREORDER_STOP, width: 24, height: 24}}>
          <HourglassBottom fontSize={'small'}/>
        </Avatar>
      )
    }
    if (gridItem['event']['type'] === pickupStart) {
      return (<div>
              <BreadLoafIcon/>
              {gridItem['event']['items'].map((val) => {
                return (<Typography key={val} fontWeight={600} fontSize={7}>{val}</Typography>)
              })}
              </div>)
    }
    return null
  }

  function renderDateBox(key, gridItem) {
    let background = 'white'
    let fontweight = 300
    const isOutOfView = gridItem['outOfView']
    if (isOutOfView) {
      background = ADJACENT_MONTH_BACKGROUND
    }
    let color = MONTH_DATE_NUMBER
    const isToday = isTodaysDate(gridItem['year'], gridItem['monthNum'], gridItem['monthDate'])
    if (isToday && !isOutOfView) {
      color = 'red'
      fontweight = 600
    }
    const hasClickableEvent = Object.hasOwn(gridItem, 'event') && [preStart, preEnd, pickupStart].includes(gridItem['event']['type'])
    const isClickable = hasClickableEvent && !isOutOfView
    let shadow = hasClickableEvent ? 6 : 0
    let hoverSettings = isClickable ? {backgroundColor: 'grey.500', boxShadow: 6} : {}
    const onClickFn = isClickable ? () => showDialog(gridItem) : () => {}

    return (
            <TableCell key={key} align={'right'}
               sx={{p: 0.45, verticalAlign: 'top', width: '3ch', height: '6.5ch',
                    backgroundColor: background, color: color,
                    boxShadow: shadow, '&:hover': hoverSettings,
                    border: '1px solid black', '& + &': {borderLeft: 'none'}}}>
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
                gap: 1.5, // spacing between avatar and text if any
              }} onClick={onClickFn}>

                <Stack direction={'column'}>
                  <Typography variant={'body2'} fontWeight={fontweight}>{gridItem['monthDate']}</Typography>
                  {renderDropEventContent(gridItem)}
                </Stack>
              </Box>
            </TableCell>
    )
  }

  /**
   * create month view grid and render the current month calendar...
   */
  const grid = createMonthGridData(getViewYear(), getViewMonthNum())

  return (
          <div>
              <Stack direction={'column'} spacing={2} useFlexGap>
                {renderViewToolbar(getViewYear(), getViewMonthNum())}
                {renderMonthTableView(grid)}
              </Stack>
            <DropCalendarDialog dialogVisible={dialogVisible}
                                title={eventTitle}
                                description={eventDescription}
                                items={eventItems}
                                dismissDialog={dismissDialog} />
          </div>
  );
}

export {DropCalendar}